import { absolutePage, buildPageMap, type Comic } from '@tanko/shared'
import type { CollectionMode, CollectionSummary } from './api-client'
import type { LibraryEntry } from './library-entries'
import { sortComics, type ComicSortOrder } from './comic-sort'
import { presentsComic, privateCategoriesOfCollection } from './private-categories'
import { pickReadingProgress, readingProgressRatio, type ProgressSummary } from './reading-progress-summary'

/**
 * 書架上的一格：一本作品，或一個把成員折起來的集合。
 *
 * 批次動作、選取、備份佇列仍然吃 `LibraryEntry`（作品清單）；只有書架與列表的渲染層
 * 改吃這個型別，因此 `ShelfItem` 的擴散止於兩個渲染元件與 entries store。
 */
export type ShelfItem =
  | { kind: 'comic'; id: string; title: string; addedAt: number; entry: LibraryEntry }
  | {
    kind: 'collection'; id: string; title: string; addedAt: number
    collection: CollectionSummary
    /** 目前清單裡有的成員；搜尋或分類篩選後可能只剩一部分，總數看 `collection.memberCount`。 */
    members: LibraryEntry[]
    /** 進度到最後一頁的成員數（本機與雲端取較新者）。 */
    readCount: number
    mode: CollectionMode
    /** 合併模式的目前位置：更新時間最新的成員進度換算成絕對頁碼，與 MergedReadingStore 同一套換算。 */
    mergedProgress: { page: number; pageCount: number } | null
    /** 封面來源：有本機那一份就用它（本機縮圖、不走網路），否則由成員資料合成雲端作品。 */
    cover: Comic
  }

export type FoldOptions = {
  query: string
  selectedCategoryIds: readonly string[]
  privateCategoryIds: ReadonlySet<string>
  /** 本機識別碼 → 進度；成員在這台裝置上時用來與雲端進度比新舊。 */
  localProgress: ReadonlyMap<string, ProgressSummary>
  order: ComicSortOrder
}

/** 由集合成員資料合成一筆雲端作品，供封面元件使用；與 cloud-library store 的 comicsInCategory 同形。 */
function memberAsCloudComic(member: CollectionSummary['members'][number]): Comic {
  return {
    id: member.comicId, libraryId: 'cloud', relPath: '', kind: 'folder', title: member.title, author: '',
    pageCount: member.pageCount, indexed: true, coverPage: member.coverPage,
    cloudComicId: member.comicId, syncState: member.syncState, addedAt: 0,
  }
}

/**
 * 集合格出現的條件。沒有篩選時一律出現；有搜尋字串時，集合標題含該字串或目前清單裡有
 * 它的成員即出現；有分類篩選時看集合自己的分類指派，成員的分類不算。
 */
function presentsCollection(collection: CollectionSummary, present: readonly LibraryEntry[], options: FoldOptions): boolean {
  // 集合格看集合自己的分類指派；隱私集合裡的分類不出現時整格不出現，與作品同一條規則。
  if (!presentsComic({ categoryIds: privateCategoriesOfCollection(collection.categoryIds, options.privateCategoryIds), privateCategoryIds: options.privateCategoryIds, selectedCategoryIds: options.selectedCategoryIds })) return false
  const query = options.query.trim().toLowerCase()
  if (options.selectedCategoryIds.length && !collection.categoryIds.some(id => options.selectedCategoryIds.includes(id))) return false
  if (query && !collection.title.toLowerCase().includes(query) && !present.length) return false
  return true
}

/** 每個成員本機與雲端取較新者之後的進度。 */
function memberProgress(collection: CollectionSummary, present: readonly LibraryEntry[], localProgress: FoldOptions['localProgress']): (ProgressSummary | null)[] {
  const localIdOf = new Map(present.map(entry => [entry.comic.cloudComicId ?? entry.id, entry.local?.id]))
  return collection.members.map(member => {
    const localId = localIdOf.get(member.comicId)
    return pickReadingProgress(localId ? localProgress.get(localId) : undefined, member.progress)
  })
}

function countRead(collection: CollectionSummary, progress: readonly (ProgressSummary | null)[]): number {
  return collection.members.filter((member, index) => {
    const picked = progress[index]
    return picked && readingProgressRatio(picked.page, member.pageCount) === 1
  }).length
}

/** 合併冊的目前位置：更新時間最新的成員；成員內頁碼超出時夾到該成員最後一頁。 */
function mergedPosition(collection: CollectionSummary, progress: readonly (ProgressSummary | null)[]): { page: number; pageCount: number } | null {
  const map = buildPageMap(collection.members.map(member => member.pageCount))
  let newest: { index: number; page: number; updatedAt: number } | undefined
  progress.forEach((picked, index) => {
    if (picked && (!newest || picked.updatedAt > newest.updatedAt)) newest = { index, page: picked.page, updatedAt: picked.updatedAt }
  })
  if (!newest || !map.pageCount) return null
  const size = collection.members[newest.index].pageCount
  if (!size) return { page: map.starts[newest.index], pageCount: map.pageCount }
  return { page: absolutePage(map, newest.index, Math.min(Math.max(0, newest.page), size - 1)), pageCount: map.pageCount }
}

/**
 * 把集合成員從清單折進集合格。純函式：輸入是合成好的作品清單與集合清單，輸出是書架的格子。
 * 成員以雲端識別碼比對；一本作品最多屬於一個集合，因此不會被折進兩格。
 */
export function foldCollections(entries: readonly LibraryEntry[], collections: readonly CollectionSummary[], options: FoldOptions): ShelfItem[] {
  const collectionOf = new Map<string, string>()
  for (const collection of collections) for (const member of collection.members) collectionOf.set(member.comicId, collection.id)

  const presentMembers = new Map<string, LibraryEntry[]>()
  const loose: ShelfItem[] = []
  for (const entry of entries) {
    const collectionId = entry.comic.cloudComicId ? collectionOf.get(entry.comic.cloudComicId) : undefined
    if (!collectionId) { loose.push({ kind: 'comic', id: entry.id, title: entry.title, addedAt: entry.addedAt, entry }); continue }
    presentMembers.set(collectionId, [...presentMembers.get(collectionId) ?? [], entry])
  }

  const cells: ShelfItem[] = []
  for (const collection of collections) {
    if (!collection.members.length) continue
    const present = presentMembers.get(collection.id) ?? []
    if (!presentsCollection(collection, present, options)) continue
    const coverMember = collection.members.find(member => member.comicId === collection.coverComicId) ?? collection.members[0]
    const coverEntry = present.find(entry => entry.comic.cloudComicId === coverMember.comicId)
    const progress = memberProgress(collection, present, options.localProgress)
    cells.push({
      kind: 'collection', id: `collection:${collection.id}`, title: collection.title, addedAt: collection.createdAt,
      collection, members: present, readCount: countRead(collection, progress),
      mode: collection.mode, mergedProgress: collection.mode === 'merged' ? mergedPosition(collection, progress) : null,
      cover: coverEntry?.local ?? coverEntry?.comic ?? memberAsCloudComic(coverMember),
    })
  }
  return sortComics([...loose, ...cells], options.order)
}

/**
 * 書架上每一格的識別碼——全選、範圍選取與修剪的依據。
 *
 * 選取的單位是格子而不是作品：集合格代表「這一格」，不展開成它的成員。原本展開之後有兩個
 * 後果——搜尋只比對到集合標題、或分類篩選只命中集合自己的分類時，成員被濾掉，集合格點了
 * 沒有反應；而且選到的是成員，批次分類指派到成員身上，書架的分類篩選看的卻是集合自己的
 * 分類，等於指派了看不到效果（2026-09 實機回報）。
 */
export function presentedCellIds(items: readonly ShelfItem[]): string[] {
  return items.map(item => item.id)
}
